import React from 'react';
import PropTypes from 'prop-types';

import {Badge, Box, Button, Stack} from 'views/components';
import {CalendarTodayOutlined, Close} from 'views/icons';
import {ConnectionBadge, translate} from 'views/module';

import AssigneePicker from './components/AssigneePicker/AssigneePicker';
import ReminderPicker from './components/ReminderPicker/ReminderPicker';
import TaskTextFields from './components/TaskTextFields';
import DatePickerQuickOptionCalendar from '../dates/DatePickerQuickOptionCalendar';

import predefinedOptionsConfigs from './components/ReminderPicker/util/predefinedOptionsConfigs';

import backend from 'utils/backend';
import moment from 'moment';
import _ from 'lodash';

const TaskEditor = (props) => {
  const {
    task,
    isEditing,
    autoFocusTitle = false,
    controlRowSx = {},
    onCancel,
    onSubmit,
  } = props;

  const [title, setTitle] = React.useState('');
  const [taskDescription, setTaskDescription] = React.useState('');
  const [taskDueDate, setTaskDueDate] = React.useState('');
  const [taskReminderDate, setTaskReminderDate] = React.useState(undefined);
  const [isDueDateOpen, setIsDueDateOpen] = React.useState(false);
  const [staticOwners, setStaticOwners] = React.useState([]);
  const [staticConnections, setStaticConnections] = React.useState([]);
  const [textOwners, setTextOwners] = React.useState([]);
  const [textConnections, setTextConnections] = React.useState([]);
  const [type, setType] = React.useState(null);
  const [skillRn, setSkillRn] = React.useState(null);
  const [admins, setAdmins] = React.useState([]);
  const [caregivers, setCaregivers] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [commandContext, setCommandContext] = React.useState(null);
  const [isCommandLoading, setIsCommandLoading] = React.useState(false);
  const dueDateButtonRef = React.useRef(null);

  const hasMentionToken = (textValue, prefix, display) => {
    if (!textValue) {
      return false;
    }

    const escapedDisplay = display.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(^|\\s)\\${prefix}${escapedDisplay}(?=$|[\\s,.;:!?\\)])`, 'i');
    return pattern.test(textValue);
  };

  const removeMentionPrefixes = (textValue, displayName, prefixes) => {
    let nextValue = textValue;
    const escapedDisplay = displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    prefixes.forEach((prefix) => {
      const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`(^|\\s)${escapedPrefix}${escapedDisplay}(?=$|[\\s,.;:!?\\)])`, 'gi');
      nextValue = nextValue.replace(pattern, `$1${displayName}`);
    });

    return nextValue.replace(/[ \t]{2,}/g, ' ');
  };

  React.useEffect(() => {
    const mentionText = `${task?.title || ''}\n${task?.description?.blob || ''}`;

    setTaskDueDate(task?.dueDate || moment().endOf('day').utc().toISOString());

    setTaskReminderDate(task?.reminderDate);

    if (task?.title) {
      setTitle(task.title);
    }
    if (task?.description?.blob) {
      setTaskDescription(task.description.blob);
    }
    if (task?.type) {
      setType(task.type);
    }
    setSkillRn(task?.skillRn || null);
    if (task?.ownerInfo) {
      setTextOwners((task.ownerInfo || [])
          .filter((owner) => hasMentionToken(mentionText, '@', `${owner?.firstName} ${owner?.lastName || ''}`.trim())));
      setStaticOwners((task.ownerInfo || [])
          .filter((owner) => !hasMentionToken(mentionText, '@', `${owner?.firstName} ${owner?.lastName || ''}`.trim())));
    }
    if (task?.connectionInfo) {
      const taskConnections = (task.connectionInfo || [])
          .filter((connection) => Boolean(connection.connectionRn));

      setTextConnections(taskConnections
          .filter((connection) => {
            const display = `${connection.firstName} ${connection.lastName || ''}`.trim();
            return hasMentionToken(mentionText, '#', display) || hasMentionToken(mentionText, '$', display);
          }));
      setStaticConnections(taskConnections
          .filter((connection) => {
            const display = `${connection.firstName} ${connection.lastName || ''}`.trim();
            return !hasMentionToken(mentionText, '#', display) && !hasMentionToken(mentionText, '$', display);
          }));

      setCaregivers(taskConnections.map((connection) => ({
        caregiverRn: connection.connectionRn,
        connectionRn: connection.connectionRn,
        caregiverName: `${connection.firstName} ${connection.lastName || ''}`.trim(),
      })));

      setClients(taskConnections.map((connection) => ({
        clientRn: connection.connectionRn,
        connectionRn: connection.connectionRn,
        clientName: `${connection.firstName} ${connection.lastName || ''}`.trim(),
      })));
    }
  }, [task]);

  React.useEffect(() => {
    if (textOwners.length === 0) {
      return;
    }

    setStaticOwners((previousOwners) => {
      const nextOwners = previousOwners.filter((owner) => {
        const ownerRn = owner?.userRn || owner?.ownerRn;
        return !textOwners.some((textOwner) => (textOwner?.userRn || textOwner?.ownerRn) === ownerRn);
      });

      return nextOwners.length === previousOwners.length ? previousOwners : nextOwners;
    });
  }, [textOwners]);

  React.useEffect(() => {
    if (textConnections.length === 0) {
      return;
    }

    setStaticConnections((previousConnections) => {
      const nextConnections = previousConnections.filter((connection) => {
        const connectionRn = connection.connectionRn;
        return !textConnections.some((textConnection) => textConnection.connectionRn === connectionRn);
      });

      return nextConnections.length === previousConnections.length ? previousConnections : nextConnections;
    });
  }, [textConnections]);

  const displayedAssignees = React.useMemo(() => _.uniqBy([
    ...[...staticOwners, ...textOwners].map((owner) => ({
      rn: owner.userRn || owner.ownerRn,
      displayName: `${owner.firstName} ${owner.lastName || ''}`.trim(),
    })),
    ...[...staticConnections, ...textConnections].map((connection) => ({
      rn: connection.connectionRn,
      displayName: connection.name || `${connection.firstName} ${connection.lastName || ''}`.trim(),
    })),
  ], (assignee) => assignee.rn), [staticConnections, staticOwners, textConnections, textOwners]);
  const assigneeCount = displayedAssignees.length;
  const taskDescriptionEditorType = task?.description?.editorType === 'MARKDOWN' ? 'MARKDOWN' : 'TEXT';

  const dueDateDisplayLabel = React.useMemo(() => {
    const parsedValue = taskDueDate ? moment(taskDueDate) : null;

    if (!parsedValue?.isValid?.()) {
      return translate('tasks.Common.TaskEditor.Date');
    }

    const localValue = parsedValue.local();

    if (localValue.isSame(moment(), 'day')) {
      return translate('tasks.Common.TaskEditor.TodayWithTime', {
        time: localValue.format('h:mmA'),
      });
    }

    return localValue.format('ddd MMM D h:mmA');
  }, [taskDueDate]);

  const handleDueDateChange = (value) => {
    setTaskDueDate(value || '');
  };

  const handleReminderDateChange = (reminderDate) => {
    setTaskReminderDate(reminderDate);
  };

  const handleTitleChange = (value) => {
    setTitle(value || '');
  };

  const handleDescriptionChange = (value) => {
    setTaskDescription(value || '');
  };

  const handleSelectAssignee = (entityType, entity) => {
    if (entityType === 'admins') {
      const adminRn = entity.userRn;

      setAdmins((previousAdmins) => (
        previousAdmins.some((admin) => admin.userRn === adminRn) ?
          previousAdmins :
          [...previousAdmins, entity]
      ));

      setStaticOwners((previousOwners) => (
        previousOwners.some((owner) => (owner.userRn || owner.ownerRn) === adminRn) ?
          previousOwners :
          [...previousOwners, {...entity, userRn: adminRn}]
      ));
    }

    if (entityType === 'caregivers') {
      const caregiverRn = entity.caregiverRn;
      const caregiverName = entity.caregiverName;

      setCaregivers((previousCaregivers) => (
        previousCaregivers.some((caregiver) => caregiver.caregiverRn === caregiverRn) ?
          previousCaregivers :
          [...previousCaregivers, entity]
      ));

      setStaticConnections((previousConnections) => (
        previousConnections.some((connection) => connection.connectionRn === caregiverRn) ?
          previousConnections :
          [...previousConnections, {connectionRn: caregiverRn, name: caregiverName, type: 'caregivers'}]
      ));
    }

    if (entityType === 'clients') {
      const clientRn = entity.clientRn;
      const clientName = entity.clientName;

      setClients((previousClients) => (
        previousClients.some((client) => client.clientRn === clientRn) ?
          previousClients :
          [...previousClients, entity]
      ));

      setStaticConnections((previousConnections) => (
        previousConnections.some((connection) => connection.connectionRn === clientRn) ?
          previousConnections :
          [...previousConnections, {connectionRn: clientRn, name: clientName, type: 'clients'}]
      ));
    }
  };

  const handleRemoveAssignee = (assignee) => {
    const displayName = assignee.displayName;

    if (assignee.rn.startsWith('RN:USER')) {
      setStaticOwners((previousOwners) => previousOwners.filter((owner) => (owner.userRn || owner.ownerRn) !== assignee.rn));
      setTextOwners((previousOwners) => previousOwners.filter((owner) => (owner.userRn || owner.ownerRn) !== assignee.rn));

      if (displayName) {
        setTitle((previousTitle) => removeMentionPrefixes(previousTitle, displayName, ['@']));
        setTaskDescription((previousDescription) => removeMentionPrefixes(previousDescription, displayName, ['@']));
      }
    } else {
      setStaticConnections((previousConnections) => previousConnections.filter((connection) => connection.connectionRn !== assignee.rn));
      setTextConnections((previousConnections) => previousConnections.filter((connection) => connection.connectionRn !== assignee.rn));

      const matchingConnections = [...staticConnections, ...textConnections]
          .filter((connection) => connection.connectionRn === assignee.rn);
      const containsCaregiver = matchingConnections.some((connection) => connection.connectionRn.startsWith('RN:CAREGIVER-V1'));
      const containsClient = matchingConnections.some((connection) => connection.connectionRn.startsWith('RN:CLIENT-V1'));
      const connectionPrefixes = [];

      if (containsCaregiver) {
        connectionPrefixes.push('#');
      }

      if (containsClient) {
        connectionPrefixes.push('$');
      }

      if (connectionPrefixes.length === 0) {
        connectionPrefixes.push('#', '$');
      }

      setTitle((previousTitle) => removeMentionPrefixes(previousTitle, displayName, connectionPrefixes));
      setTaskDescription((previousDescription) => removeMentionPrefixes(previousDescription, displayName, connectionPrefixes));
    }
  };

  // removes exclamation marks before words and reminder codes from a string
  const removeUnwantedSymbolsAndCodes = (textValue) => {
    if (!textValue) {
      return '';
    }

    // convert reminder codes into an array that can be safely converted into a regex
    const reminderCodes = predefinedOptionsConfigs
        .map((predefinedOptionConfig) => predefinedOptionConfig.code)
        .sort((left, right) => right.length - left.length)
        .map((code) => code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); // escapes any special chars so they're not interpreted as regex syntax

    // remove exclamation mark in any word that starts with one e.g., !call -> call and maintain proper spacing
    let nextValue = textValue.replace(/(^|\s)!/g, '$1');

    // converts reminder code array into a string like this ~12hb|~1hb|~2hb...
    const reminderPattern = new RegExp(
        `~(?:${reminderCodes.join('|')})`,
        'g',
    );

    // replace any of these: ~12hb|~1hb|~2hb... with empty string
    nextValue = nextValue.replace(reminderPattern, '');

    return nextValue;
  };

  const handleSubmitTask = () => {
    if (!title || !moment(taskDueDate).isValid()) {
      return;
    }

    const ownerRns = [...new Set([...staticOwners, ...textOwners].map((owner) => owner.userRn || owner.ownerRn))];
    const connectionRns = [...new Set([...staticConnections, ...textConnections].map((connection) => connection.connectionRn))];

    const payload = {
      title: removeUnwantedSymbolsAndCodes(title).trim(),
      description: {
        blob: removeUnwantedSymbolsAndCodes(taskDescription).trim(),
        editorType: taskDescriptionEditorType,
      },
      dueDate: taskDueDate,
      reminderDate: taskReminderDate,
      ownerRns,
      connections: connectionRns,
      skillRn,
      type,
      status: task?.status,
    };
    onSubmit(payload);
  };

  const handleEditorKeyDown = (event) => {
    if (event.defaultPrevented) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmitTask();
    }
  };

  const hasDueDate = Boolean(taskDueDate && moment(taskDueDate).isValid());
  const canSubmit = Boolean(title) && moment(taskDueDate).isValid();

  React.useEffect(() => {
    let mounted = true;

    const loadAdmins = async () => {
      try {
        const filter = [
          {key: 'status', value: ['ACTIVE']},
          {key: 'role', value: 'ADMIN'},
        ];
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.user.queryUser(filter, sortBy);

        if (mounted) {
          setAdmins(result?.list ?? []);
        }
      } catch (error) {
        console.error('Failed to load admin users', error);
      }
    };

    loadAdmins();

    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (commandContext?.trigger !== '#') {
      return;
    }

    const searchQuery = commandContext?.query || '';
    const isFirstLoad = searchQuery.length === 0;
    let mounted = true;
    setIsCommandLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const filter = [];

        if (searchQuery) {
          filter.push({key: 'caregiverName', value: `${searchQuery}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.caregiver.queryCaregiver(filter, sortBy);

        if (mounted) {
          const nextCaregivers = result.list || [];
          setCaregivers((previousCaregivers) => _.uniqBy(
              [...nextCaregivers, ...previousCaregivers],
              (caregiver) => caregiver.caregiverRn,
          ));
        }
      } catch (error) {
        console.error('Failed to load caregivers for commands', error);
      } finally {
        if (mounted) {
          setIsCommandLoading(false);
        }
      }
    }, isFirstLoad ? 0 : 250);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [commandContext?.query, commandContext?.trigger]);

  React.useEffect(() => {
    if (commandContext?.trigger !== '$') {
      return;
    }

    const searchQuery = commandContext?.query || '';
    const isFirstLoad = searchQuery.length === 0;
    let mounted = true;
    setIsCommandLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const filter = [
          {
            key: 'status',
            value: [
              'ACTIVE',
              'LEAD',
              'DETAILS_NEEDED',
              'CG_SEARCH',
              'CG_SEARCH_ACTIVE',
              'CG_PROFILE_SENT',
              'MEET_GREET',
              'STALLED',
            ],
          },
        ];

        if (searchQuery) {
          filter.push({key: 'clientName', value: `${searchQuery}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.client.queryClient(filter, sortBy);

        if (mounted) {
          const nextClients = result.list || [];
          setClients((previousClients) => _.uniqBy(
              [...nextClients, ...previousClients],
              (client) => client.clientRn,
          ));
        }
      } catch (error) {
        console.error('Failed to load clients for commands', error);
      } finally {
        if (mounted) {
          setIsCommandLoading(false);
        }
      }
    }, isFirstLoad ? 0 : 250);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [commandContext?.query, commandContext?.trigger]);

  return (
    <Box sx={{position: 'relative'}}>
      <Stack
        onKeyDown={handleEditorKeyDown}
        spacing={1.5}
        sx={{
          border: '1px solid #E8E4DE',
          borderRadius: '10px',
          p: 1.5,
          backgroundColor: '#FFFFFF',
        }}
      >
        <TaskTextFields
          title={title}
          taskDescription={taskDescription}
          autoFocusTitle={autoFocusTitle}
          admins={admins}
          caregivers={caregivers}
          clients={clients}
          dueDate={taskDueDate}
          onDueDateChange={handleDueDateChange}
          onTitleChange={handleTitleChange}
          onDescriptionChange={handleDescriptionChange}
          onOwnersChange={setTextOwners}
          onConnectionsChange={setTextConnections}
          onTypeChange={setType}
          onSkillChange={setSkillRn}
          onReminderDateChange={setTaskReminderDate}
          currentSkillRn={skillRn}
          onCommandContextChange={setCommandContext}
          commandLoading={isCommandLoading}
        />
        <Box
          sx={{
            borderTop: '1px solid #E8E4DE',
            pt: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          <Stack
            direction='row'
            spacing={1}
            useFlexGap
            sx={{
              flex: '1 1 280px',
              minWidth: 0,
              flexWrap: 'wrap',
              alignItems: 'center',
              rowGap: 1,
              ...controlRowSx,
            }}
          >
            <Button
              variant='outlined'
              anchorref={dueDateButtonRef}
              onClick={() => setIsDueDateOpen(true)}
              startIcon={<CalendarTodayOutlined sx={{fontSize: 16}} />}
              sx={{
                'borderColor': '#E8E4DE',
                'color': hasDueDate ? '#2563EB' : '#7A756E',
                'backgroundColor': 'transparent',
                'borderRadius': '8px',
                'textTransform': 'none',
                'minWidth': 0,
                'px': 1.5,
                'py': 0.5,
                '& .MuiButton-startIcon': {
                  color: hasDueDate ? '#2563EB' : '#A8A49E',
                },
              }}
            >
              {dueDateDisplayLabel}
            </Button>
            <DatePickerQuickOptionCalendar
              anchorRef={dueDateButtonRef}
              open={isDueDateOpen}
              onClose={() => setIsDueDateOpen(false)}
              onChange={handleDueDateChange}
              value={taskDueDate}
            />
            <AssigneePicker
              assigneeCount={assigneeCount}
              onSelectAssignee={handleSelectAssignee}
            />
            <ReminderPicker
              dueDate={taskDueDate}
              reminderDate={taskReminderDate}
              handleReminderDateChange={handleReminderDateChange}
            />
            {
              assigneeCount > 0 &&
              <Stack
                direction='row'
                spacing={0.5}
                useFlexGap
                sx={{
                  width: {xs: '100%', lg: 'auto'},
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  rowGap: 1,
                }}
              >
                {
                  displayedAssignees.map((assignee) => (
                    <Badge
                      key={`${assignee.rn}`}
                      overlap='circular'
                      anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                      sx={{
                        '& .MuiBadge-badge': {
                          top: -2,
                          right: -2,
                          transform: 'translate(35%, -35%)',
                        },
                      }}
                      badgeContent={
                        <Box
                          component='button'
                          type='button'
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveAssignee(assignee);
                          }}
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            border: '1px solid #D5D1CB',
                            backgroundColor: '#FFFFFF',
                            color: '#7A756E',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0,
                            cursor: 'pointer',
                          }}
                        >
                          <Close sx={{fontSize: 10}} />
                        </Box>
                      }
                    >
                      <ConnectionBadge
                        connection={{
                          connectionRn: assignee.rn,
                          firstName: assignee.displayName.split(/\s+/)[0] || '',
                          lastName: assignee.displayName.split(/\s+/).slice(1).join(' '),
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: 11,
                        }}
                      />
                    </Badge>
                  ))
                }
              </Stack>
            }
          </Stack>
          <Stack
            direction='row'
            spacing={1}
            sx={{
              flex: {xs: '1 1 100%', lg: '0 0 auto'},
              width: {xs: '100%', lg: 'auto'},
              ml: {lg: 'auto'},
              justifyContent: {xs: 'stretch', sm: 'flex-end'},
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant='text'
              onClick={onCancel}
              sx={{
                flex: {xs: 1, sm: '0 0 auto'},
                color: '#7A756E',
                textTransform: 'none',
              }}
            >
              {translate('tasks.Common.TaskEditor.Cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmitTask}
              disabled={!canSubmit}
              sx={{
                flex: {xs: 1, sm: '0 0 auto'},
                backgroundColor: '#DA7955',
                color: '#FFFFFF',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              {isEditing ? translate('tasks.Common.TaskEditor.SaveTask') : translate('tasks.Common.TaskEditor.AddTask')}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

TaskEditor.propTypes = {
  task: PropTypes.object,
  isEditing: PropTypes.bool,
  autoFocusTitle: PropTypes.bool,
  controlRowSx: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default React.memo(TaskEditor);
